import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material';
import deepEqual from 'fast-deep-equal';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useSettingIds } from '~/components/settings';
import { i18n } from '~/lib/i18n';
import type { PartialAction } from '../../lib/utils';

const options = [
  // Content playback
  { command: 'play_overlay' },
  { command: 'stop_overlay' },
  { command: 'play_show' },
  { command: 'trigger_view' },
  // Channel control
  { command: 'tune_channel' },
  // Device power controls (most disruptive last)
  { command: 'arbitrary', arbitrary: 'tv_on' },
  { command: 'arbitrary', arbitrary: 'tv_off' },
  { command: 'reboot' },
] as const;

const isOptionEqualToValue = (option: PartialAction, value: PartialAction) =>
  option.command === 'arbitrary' ? deepEqual(option, value) : option.command === value.command;

const getOptionLabel = (option: PartialAction): string => {
  if (option.command === 'arbitrary') {
    return option.arbitrary === 'tv_off'
      ? i18n.t('pages.device_signals.settings.actions.tv_off')
      : i18n.t('pages.device_signals.settings.actions.tv_on');
  }
  return i18n.t(`pages.device_signals.settings.actions.${option.command}`);
};

export interface ActionAutocompleteProps
  extends Omit<AutocompleteProps<PartialAction, false, false, false>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
}

export const ActionAutocomplete = ({
  error,
  helperText,
  name,
  ...autocompleteProps
}: ActionAutocompleteProps) => {
  const { t } = useTranslation();
  // Inside a settings row the row's label names the input.
  const ids = useSettingIds();

  return (
    <Autocomplete<PartialAction>
      id={ids?.inputId}
      fullWidth
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_action'),
            ids != null,
          )}
        />
      )}
      {...autocompleteProps}
    />
  );
};
